import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Image as ImageIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { SignaturesManager } from '@/components/settings/SignaturesManager';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { organizationSettingsService } from '@/services/organizationSettings.service';
import { generalSettingsSchema, type GeneralSettingsFormData } from '@/lib/validators';
import { INVOICE_THEME_PRESETS, APP_CONFIG } from '@/lib/constants';
import { cn } from '@/lib/cn';
import type { OrganizationSettings } from '@/types/table';
import { AssetUploader, ColorPickerField, MAX_IMAGE_SIZE } from './_shared';

/**
 * General tab — settings every document type shares: company identity,
 * tax id + identifier label, address, logo, theme, signatures, and the
 * company-wide numbering prefix.
 */
export function GeneralTab() {
  const { settings, update, uploadAsset, removeAsset } = useOrganizationSettings();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);

  const form = useForm<GeneralSettingsFormData>({
    resolver: zodResolver(generalSettingsSchema),
    defaultValues: {
      companyName: 'Your Company',
      companyIdentifierLabel: 'GSTIN',
      primaryColor: '#010b17',
      accentColor: '#a855f7',
      highlightColor: '#22d3ee',
      companyPrefix: '',
      taxMode: 'SINGLE',
      taxLabel: 'Tax',
    },
  });

  useEffect(() => {
    if (!settings) return;
    form.reset({
      companyName: settings.companyName ?? '',
      companyEmail: settings.companyEmail ?? '',
      companyPhone: settings.companyPhone ?? '',
      companyWebsite: settings.companyWebsite ?? '',
      taxId: settings.taxId ?? '',
      companyIdentifierLabel: settings.companyIdentifierLabel ?? 'GSTIN',
      addressLine1: settings.addressLine1 ?? '',
      addressLine2: settings.addressLine2 ?? '',
      city: settings.city ?? '',
      state: settings.state ?? '',
      country: settings.country ?? '',
      postalCode: settings.postalCode ?? '',
      primaryColor: settings.primaryColor ?? '#010b17',
      accentColor: settings.accentColor ?? '#a855f7',
      highlightColor: settings.highlightColor ?? '#22d3ee',
      companyPrefix: settings.companyPrefix ?? '',
      taxMode: settings.taxMode ?? 'SINGLE',
      taxLabel: settings.taxLabel ?? 'Tax',
    });
  }, [settings, form]);

  const values = form.watch();
  const cacheBuster = settings ? new Date(settings.updatedAt).getTime() : 0;
  const liveLogoUrl = logoPreview ?? (settings?.logoFileKey ? organizationSettingsService.assetUrl('logo', cacheBuster) : null);

  const onSelectLogo = (f: File | null | undefined) => {
    if (!f) return;
    if (f.size > MAX_IMAGE_SIZE) { alert('Image must be 5 MB or smaller'); return; }
    const url = URL.createObjectURL(f);
    setLogoPreview(url);
    uploadAsset.mutate({ kind: 'logo', file: f }, {
      onSuccess: () => { URL.revokeObjectURL(url); setLogoPreview(null); },
      onError: () => { URL.revokeObjectURL(url); setLogoPreview(null); },
    });
  };

  const onRemoveLogo = () => {
    if (logoPreview) { URL.revokeObjectURL(logoPreview); setLogoPreview(null); }
    removeAsset.mutate('logo');
  };

  const applyPreset = (preset: typeof INVOICE_THEME_PRESETS[number]) => {
    form.setValue('primaryColor', preset.primary, { shouldDirty: true });
    form.setValue('accentColor', preset.accent, { shouldDirty: true });
    form.setValue('highlightColor', preset.highlight, { shouldDirty: true });
  };

  const onSubmit = (data: GeneralSettingsFormData) => update.mutate(data as Partial<OrganizationSettings>);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader><CardTitle className="text-base">Company Identity</CardTitle><CardDescription>Shown in the header of every document</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <FormField form={form} name="companyName" label="Company Name" required />
          <div className="grid grid-cols-2 gap-4">
            <FormField form={form} name="companyEmail" label="Email" type="email" />
            <FormField form={form} name="companyPhone" label="Phone" />
          </div>
          <FormField form={form} name="companyWebsite" label="Website" />
          <div className="grid grid-cols-2 gap-4">
            <FormField form={form} name="companyIdentifierLabel" label="Tax ID Label" description="e.g. GSTIN, CIN, EIN, ABN, VAT" />
            <FormField form={form} name="taxId" label="Tax ID Value" description="The identifier number itself" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tax on documents</CardTitle>
          <CardDescription>How tax prints on quotations and invoices. Applies to documents saved from now on — issued ones keep what they printed.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormSelect
              form={form}
              name="taxMode"
              label="Tax lines"
              options={[
                { label: 'One line (e.g. GST 18%)', value: 'SINGLE' },
                { label: 'GST split — CGST + SGST / IGST', value: 'SPLIT' },
              ]}
            />
            <FormField form={form} name="taxLabel" label="Tax name" description="Printed on the single line, e.g. GST, VAT" />
          </div>
          <p className="text-xs text-muted-foreground">
            {values.taxMode === 'SPLIT'
              ? `A client in ${values.state || 'your state'} is billed CGST + SGST at half the rate each; any other state, or abroad, is billed IGST. Each document can override this.`
              : 'Tax prints as one line. Any single quotation or invoice can still choose CGST + SGST or IGST under "Tax lines".'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Address</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <FormField form={form} name="addressLine1" label="Address Line 1" />
          <FormField form={form} name="addressLine2" label="Address Line 2" />
          <div className="grid grid-cols-2 gap-4">
            <FormField form={form} name="city" label="City" />
            <FormField form={form} name="state" label="State / Region" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField form={form} name="country" label="Country" />
            <FormField form={form} name="postalCode" label="Postal Code" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Logo</CardTitle><CardDescription>PNG, JPG, or WebP — up to {(APP_CONFIG.MAX_FILE_SIZE / 1024 / 1024).toFixed(0)} MB. SVG is not supported in PDFs.</CardDescription></CardHeader>
        <CardContent>
          <AssetUploader
            label="Logo"
            icon={<ImageIcon className="h-5 w-5" />}
            url={liveLogoUrl}
            inputRef={logoInput}
            onSelect={onSelectLogo}
            onRemove={settings?.logoFileKey ? onRemoveLogo : undefined}
            busy={uploadAsset.isPending && uploadAsset.variables?.kind === 'logo'}
          />
        </CardContent>
      </Card>

      <SignaturesManager />

      <Card>
        <CardHeader><CardTitle className="text-base">Color Theme</CardTitle><CardDescription>Pick a preset or customize hex colors — applies to all document PDFs</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {INVOICE_THEME_PRESETS.map((preset) => {
              const active = values.primaryColor === preset.primary && values.accentColor === preset.accent && values.highlightColor === preset.highlight;
              return (
                <button key={preset.name} type="button" onClick={() => applyPreset(preset)}
                  className={cn('rounded-md border px-3 py-2 flex items-center gap-2 text-xs transition-colors', active ? 'border-primary ring-2 ring-primary/30' : 'border-input hover:border-foreground/30')}>
                  <span className="flex">
                    <span className="h-4 w-4 rounded-l" style={{ background: preset.primary }} />
                    <span className="h-4 w-4" style={{ background: preset.accent }} />
                    <span className="h-4 w-4 rounded-r" style={{ background: preset.highlight }} />
                  </span>
                  <span>{preset.name}</span>
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <ColorPickerField form={form} name="primaryColor" label="Primary (Header)" />
            <ColorPickerField form={form} name="accentColor" label="Accent (Title / Bands)" />
            <ColorPickerField form={form} name="highlightColor" label="Highlight (Paid)" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Company Prefix</CardTitle>
          <CardDescription>Prepended to ALL document numbers. e.g. <code className="text-foreground">ZY</code> → ZY-INV-2026-…, ZY-QT-2026-…, ZY-AGR-2026-…. Leave blank to skip.</CardDescription>
        </CardHeader>
        <CardContent>
          <FormField form={form} name="companyPrefix" label="Company Prefix" />
        </CardContent>
      </Card>

      <Separator />
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={() => form.reset()}>Reset</Button>
        <Button type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save General Settings'}</Button>
      </div>
    </form>
  );
}
